import {
  ref,
  getCurrentInstance,
  ComponentInternalInstance,
  computed,
  reactive,
  UnwrapRef,
  Ref,
  onMounted,
} from '@vue/composition-api';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import shareAccessTokenManager from '@/renderer/scripts/utils/AccessTokenManager/share';
import storage from '@/renderer/scripts/utils/storage';
import tips from '@/renderer/scripts/utils/tips';
import { getDeviceName, getTencentCaptchaLanguage } from '@/renderer/scripts/utils/tools';

interface Handler {
  status: UnwrapRef<{
    isForbidden: boolean;
    isExpired: boolean;
    isExtracted: boolean;
    isEnterpriseExpired: boolean;
  }>;
  extractCode: Ref<string>;
  extractLoading: Ref<boolean>;
  spaceId: Ref<string>;
  safeStatus: Ref<number>;
  shareFileSpaceOrgId: Ref<number>;
  methods: {
    initShareId: () => void;
    initData: () => Promise<boolean>;
    onExtractFiles: (isInit: boolean) => Promise<void>;
  };
}
export default function handler(): Handler {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const { $store } = vm;
  const spaceId = ref<SMH.SpaceId>('');
  const extractCode = computed<string>({
    get() {
      shareAccessTokenManager.setData({
        extractionCode: $store.getters.extractCode,
        cookieShareToken: $store.getters.cookieShareToken,
      });
      return $store.getters.extractCode;
    },
    set(val) {
      shareAccessTokenManager.setData({
        extractionCode: val,
      });
      $store.commit('SET_EXTRACT_CODE', val);
    },
  });
  const extractLoading = ref(false);
  const safeStatus = ref(0);
  // 分享文件本身的spaceOrgId
  const shareFileSpaceOrgId = ref(-1);
  const status = reactive({
    isForbidden: false,
    isExpired: false,
    isInvalid: false,
    isExtracted: false,
    isEnterpriseExpired: false,
  });

  // 防水墙
  let captcha: null | any = null;
  const captchaInfo = reactive({
    captchaTicket: '',
    captchaRandstr: '',
  });
  const methods = {
    initShareId() {
      const shareToken = vm.$route.params?.shareToken || vm.$route.query?.shareToken;
      $store.commit('SET_SHARE_ID', shareToken);
    },
    async initData() {
      if (!extractCode.value) {
        storage.del(storage.StoreKey.SHARE_LINK_ROOT_PATH);
      }
      const shareToken = $store.getters.shareId;
      try {
        const data = await $store.dispatch('GetSharedLinkDetail', shareToken);
        shareAccessTokenManager.setData({
          userId: $store.getters.isLogined ? $store.getters.userId : undefined,
        });
        if (!data.needExtractionCode) {
          extractCode.value = '';
        }
        shareFileSpaceOrgId.value = data.orgId;
        safeStatus.value = data.status;
        let expired = dayjs(data.expireTime).isBefore(dayjs());
        if (data.isPermanent) {
          expired = false;
        }
        if (!expired) {
          await methods.onExtractFiles(true);
          // 如果调用onExtractFiles方法存在风险文件需要直接return false
          if (!status.isExtracted) {
            return false;
          }
          return true;
        }
        if (data.disabled && !expired) {
          status.isForbidden = true;
          return false;
        }
        status.isExpired = true;
        return false;
      } catch (err: any) {
        extractLoading.value = false;
        if (err?.code === 'ShareLinkIsDisabled' || err?.code === 'OrganizationNotEnableShare') {
          status.isForbidden = true;
        } else if (err?.code === 'SharingOrganizationServiceTimeExpired') {
          status.isEnterpriseExpired = true;
        } else if (err?.code === 'ShareInfoNotFound') {
          status.isInvalid = true;
        } else {
          status.isExpired = true;
        }
        return false;
      }
    },
    async onExtractFiles(isInit: boolean) {
      if (!extractCode.value && $store.getters.needExtractionCode) return;
      extractLoading.value = true;
      try {
        const shareToken = $store.getters.shareId;
        let userToken;
        if (!Cookies.get('USER_TOKEN')) {
          userToken = Cookies.get('VISTOR_USER_TOKEN');
        }
        const deviceId = getDeviceName();
        const { data } = await smh.userAPI.verifyExtractionCode({
          shareToken,
          deviceId,
          extractionCode: extractCode.value,
          ...(captchaInfo.captchaTicket && captchaInfo),
          ...(userToken && {
            userToken,
          }),
        });
        shareAccessTokenManager.set(shareToken, data);
        spaceId.value = data.spaceId;
        status.isExtracted = true;
        extractLoading.value = false;
      } catch (error: any) {
        extractCode.value = '';
        status.isExtracted = false;
        extractLoading.value = false;
        if (isInit && typeof isInit === 'boolean') return;
        if (error?.code === 'ShareInfoNotFound') {
          tips.error(i18n_t('当前分享已失效'));
        } else if (error?.code === 'ShareExtractionCodeInvalid') {
          tips.error(i18n_t('提取码有误，请重新输入'));
        } else if (['GraphicCaptchaNotFound', 'InvalidGraphicCaptcha'].includes(error?.code)) {
          // 需要图形验证码之后重新发送
          captcha.show();
        } else {
          tips.error(error); // 添加禁用ShareLinkIsDisabled，且兜底
        }
      }
    },

    // 初始化图形验证码
    // 图形验证码在发短信前或登录失败时都会触发
    initCaptcha() {
      try {
        // @ts-ignore
        captcha = new TencentCaptcha(
          '2091142919',
          // @ts-ignore
          res => {
            // 清空
            Object.assign(captchaInfo, {
              captchaTicket: '',
              captchaRandstr: '',
            });
            captcha.destroy();
            // 验证成功
            if (res.ret === 0) {
              Object.assign(captchaInfo, {
                captchaTicket: res.ticket || '',
                captchaRandstr: res.randstr || '',
              });
              methods.onExtractFiles(false);
            }
          },
          {
            userLanguage: getTencentCaptchaLanguage(i18n.locale),
          },
        );
      } catch (e) {
        logger.ui('TencentCaptcha初始化失败');
      }
    },
  };
  onMounted(() => {
    methods.initCaptcha();
  });
  return {
    status,
    spaceId,
    extractLoading,
    extractCode,
    safeStatus,
    shareFileSpaceOrgId,
    methods,
  };
}
